import parseRenderingContext from '@utils/parse-rendering-context';
import CartLine from './cart-line';
import CartModal from './cart-modal';
import { alert } from './templates/cart-landing';
import { defaults } from './cart-mapping';
import store from '@stores/cart/cart-store';

export default class CartLanding {
    constructor($originEl, context, error) {
        this.context = { ...defaults.context, ...context };

        this.$el = $originEl;

        this.$empty = this.$el.find('[data-region="empty"]');
        this.$heading = this.$el.find('[data-region="heading"]');
        this.$note = this.$el.find('[data-region="note"]');
        this.$promotionsnote = this.$el.find('[data-region="promotionsnote"]');
        this.$loader = this.$el.find('[data-region="landingLoader"]');
        this.$prescription = this.$el.find('[data-region="prescription"]');
        this.$render = this.$el.find('[data-region="render"]');

        this.CartLine = CartLine.extendPrompt(() => this.unsavedLinePrompt());

        this.error = error;
        this.hasError = !!error;

        this.modal = null;

        this.lines = [];

        this.init();
    }

    init() {
        if(!this.hasError) {
            this.setupLines();
            this.renderLines();
            this.setupAccessibility();
            this.setupListeners();
            this.setupModal();
        } else {
            this.renderError();
        }
    }

    removeCartRegions() {
        this.$note.hide();
        this.$prescription.hide();
        this.$render.hide();
    }

    waitForDelay(delay = 0) {
        return new Promise((resolve) => {
            setTimeout(() => resolve(), delay);
        });
    }

    async removeDeletedLines(deletedLines = []) {
        const destroyPromises = deletedLines.map((component, index) => {
            const delay = index * 150;
            return this.waitForDelay(delay).then(() => component.destroy());
        });

        await Promise.all(destroyPromises);

        if(store.getters.isEmpty) {
            this.removeCartRegions();
            this.renderEmptyCartRegions();
        }
    }

    renderAddedLines() {
        store.getters.linesAdded.forEach((id) => {
            const index = store.getters.lineIds.indexOf(id);
            const prevIndex = index - 1;
            const component = this.lines[index];

            if (prevIndex > -1) {
                const prevComponent = this.lines[prevIndex];
                prevComponent.$el.after(component.$el);
            } else {
                this.$render.prepend(component.$el);
            }
        });
    }

    renderEmptyCartRegions() {
        this.$heading.html(this.context.YourShoppingCartEmpty);
        this.$empty.show();
    }

    renderError() {
        this.$render.html(alert(this.error)).show();
        this.$loader.remove();
    }

    renderCartRegions() {
        this.$note.html(this.context.ItemHold);
        if(this.context.PromotionNoteIcon && this.context.PromotionNote){
            let pnote = `<img src="${this.context.PromotionNoteIcon}" /><div>${this.context.PromotionNote}</div>`;
            this.$promotionsnote.html(pnote);
            this.$promotionsnote.addClass("mb-3");
        }else{
            this.$promotionsnote.hide();
        }
        this.$prescription.slideDown();
    }

    renderLines() {
        if (this.lines.length > 0) {
            this.$loader.remove();

            this.lines.forEach((component) => {
                this.$render.append(component.$el);
            });

            this.$render.animate({ height: 'toggle' }, 'linear');
        }
    }

    setupAccessibility() {
        this.$el.attr('aria-live', 'polite');
    }

    setupModal() {
        this.modal = new CartModal({
            body: this.context.ModalLineUpdateBody,
            buttonContinue: this.context.ModalLineUpdateContinue,
            buttonUpdate: this.context.ModalLineUpdateApplyUpdates,
            heading: this.context.ModalLineUpdateHeading
        });
    }

    setupLines() {
        if (!store.getters.isEmpty) {
            let displayRxRequired = false;
            this.renderCartRegions();

            store.getters.lines.forEach(line => {
                this.lines.push(
                    new this.CartLine(null, this.context, line)
                );
                if (line.rxRequired) displayRxRequired = true;
            });

            !displayRxRequired ? this.$prescription.hide() : '';

        } else {
            this.renderEmptyCartRegions();
        }

        this.$loader.remove();
    }

    setupListeners() {
        store.watch('lines', () => this.updateLines());
    }

    async unsavedLinePrompt() {
        return await this.modal.prompt();
    }

    updateLines() {
        const deletedLines = store.getters.linesRemoved.map((id) => {
            const index = store.state.cachedLineIds.indexOf(id);
            return this.lines[index];
        });

        this.lines =
            store.getters.lines
                .map((line) => {
                    const prevLine = this.lines.find((prevLine) => prevLine.lineId === line.lineId);
                    return prevLine || new CartLine(null, this.context, line);
                });

        this.renderAddedLines();
        this.removeDeletedLines(deletedLines);
        this.hasRxRequired();
    }

    hasRxRequired() {
        let displayRxRequired = false;

        store.getters.lines.forEach(line => {
            if (line.rxRequired) displayRxRequired = true;
        });

        !displayRxRequired ? this.$prescription.hide() : '';
    }
}

$(() => {
    const rootEls = document.querySelectorAll('.js-cart-landing');
    rootEls.forEach((el) => {
        const $el = $(el);
        const renderingContext = parseRenderingContext(el);

        if(!store.state.loaded) {
            const unsubscribe = store.watch('loaded', (...[state,, loaded]) => {
                if (loaded) {
                    new CartLanding($el, renderingContext, state.error);
    
                    unsubscribe();
                }
            });
        }
    });

    if(rootEls.length) {
        store.actions.getCart();
    }
});
